import {
  Badge,
  Box,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  HStack,
  Icon,
  Skeleton,
  Text,
  VStack,
} from '@chakra-ui/react';
import Tooltip from '../../../../components/Tooltip';
import { motion } from 'framer-motion';
import { format, formatDistanceToNow } from 'date-fns';
import esLocale from 'date-fns/locale/es';
import {
  FiArrowRight,
  FiCalendar,
  FiCheckCircle,
  FiEdit3,
  FiGlobe,
  FiMonitor,
  FiPlusCircle,
  FiUser,
  FiXCircle,
} from 'react-icons/fi';
import {
  EventoReserva,
  TipoEvento,
  momentoDe,
  nombreDeCampo,
  valorLegible,
  describirDondeOcurrio,
} from '~/core/tracking/reservationEvents';
import { useHistorialReserva } from '~/hooks/useReservationEvents';

const MotionBox = motion(Box);

/** Aspecto de cada tipo de evento: icono, color y título. */
const ASPECTO: Record<TipoEvento, { icono: typeof FiPlusCircle; color: string; titulo: string }> = {
  created: { icono: FiPlusCircle, color: 'green', titulo: 'Reserva creada' },
  modified: { icono: FiEdit3, color: 'orange', titulo: 'Reserva modificada' },
  cancelled: { icono: FiXCircle, color: 'red', titulo: 'Reserva cancelada' },
  status: { icono: FiCheckCircle, color: 'blue', titulo: 'Cambio de estado' },
};

const fechaLegible = (evento: EventoReserva) => {
  // Cuando hay nota, la fecha que lleva el evento existe solo para ordenarlo.
  // Se muestra la nota, nunca una hora que nadie llegó a guardar.
  if (evento.notaFecha) return { absoluta: evento.notaFecha.corta, relativa: '' };
  const ms = momentoDe(evento);
  if (!ms) return { absoluta: 'fecha desconocida', relativa: '' };
  const fecha = new Date(ms);
  return {
    absoluta: format(fecha, "d 'de' MMMM 'de' yyyy, h:mm a", { locale: esLocale }),
    relativa: formatDistanceToNow(fecha, { locale: esLocale, addSuffix: true }),
  };
};

/** Quién actuó, dicho en una línea. */
const Autor = ({ evento }: { evento: EventoReserva }) => {
  const { actor } = evento;
  // De algunos hechos antiguos solo queda el rastro indirecto: se sabe que
  // pasaron, no quién los hizo. Decir "alguien del equipo" sería inventarlo.
  if (actor?.kind === 'unknown') {
    return (
      <HStack spacing={1.5}>
        <Icon as={FiUser} color='gray.300' boxSize={3.5} />
        <Text fontSize='xs' color='gray.500' fontStyle='italic'>
          No se registró quién
        </Text>
      </HStack>
    );
  }
  if (actor?.kind === 'customer') {
    return (
      <HStack spacing={1.5}>
        <Icon as={FiUser} color='gray.400' boxSize={3.5} />
        {/* Solo QUIÉN. De dónde vino lo dice la línea de origen, que sí lo
            sabe: decirlo aquí afirmaba que venía del correo aunque hubiera
            entrado por la web del cliente. */}
        <Text fontSize='xs' color='gray.600'>
          El cliente
        </Text>
      </HStack>
    );
  }
  const quien = actor?.name || actor?.email;
  return (
    <HStack spacing={1.5}>
      <Icon as={FiUser} color='blue.400' boxSize={3.5} />
      <Text fontSize='xs' color='gray.700' fontWeight='medium'>
        {quien || 'Alguien del equipo'}
      </Text>
      {actor?.email && quien !== actor.email && (
        <Text fontSize='xs' color='gray.500'>
          · {actor.email}
        </Text>
      )}
    </HStack>
  );
};

/** Desde dónde se hizo. */
const Origen = ({ evento }: { evento: EventoReserva }) => {
  const { origin } = evento;
  if (!origin) return null;
  const donde = describirDondeOcurrio(origin, evento.derived, evento.origenNota);
  return (
    <HStack spacing={1.5} color='gray.500'>
      <Icon as={origin.channel === 'dashboard' ? FiMonitor : FiGlobe} boxSize={3.5} />
      <Text fontSize='xs'>{donde}</Text>
      {origin.device && origin.device !== 'no registrado' && (
        <Text fontSize='xs' color='gray.400'>
          · {origin.device}
        </Text>
      )}
    </HStack>
  );
};

/** Los campos que cambiaron, uno por línea: antes → después. */
const Cambios = ({ evento }: { evento: EventoReserva }) => {
  if (!evento.changes?.length) return null;
  return (
    <VStack align='stretch' spacing={1} mt={2} pl={0.5}>
      {evento.changes.map((c) => (
        <HStack key={c.field} spacing={2} fontSize='xs' flexWrap='wrap'>
          <Text color='gray.500' minW='fit-content'>
            {nombreDeCampo(c.field)}
          </Text>
          <Badge colorScheme='gray' variant='subtle' fontWeight='normal'>
            {valorLegible(c.field, c.before)}
          </Badge>
          <Icon as={FiArrowRight} color='gray.400' boxSize={3} />
          <Badge colorScheme='green' variant='subtle' fontWeight='normal'>
            {valorLegible(c.field, c.after)}
          </Badge>
        </HStack>
      ))}
    </VStack>
  );
};

const Evento = ({ evento, indice, ultimo }: { evento: EventoReserva; indice: number; ultimo: boolean }) => {
  const aspecto = ASPECTO[evento.type] ?? ASPECTO.status;
  const { absoluta, relativa } = fechaLegible(evento);

  return (
    <MotionBox
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.28, delay: Math.min(indice * 0.05, 0.4) }}
      position='relative'
      pl={9}
      pb={ultimo ? 0 : 6}
    >
      {/* Línea vertical que une los hitos */}
      {!ultimo && (
        <Box position='absolute' left='15px' top='28px' bottom='0' width='2px' bg='gray.200' />
      )}
      <Flex
        position='absolute'
        left='0'
        top='2px'
        w='32px'
        h='32px'
        align='center'
        justify='center'
        borderRadius='full'
        bg={`${aspecto.color}.50`}
        borderWidth='1px'
        borderColor={`${aspecto.color}.200`}
      >
        <Icon as={aspecto.icono} color={`${aspecto.color}.500`} boxSize={4} />
      </Flex>

      <HStack spacing={2} align='baseline' flexWrap='wrap'>
        <Text fontWeight='semibold' fontSize='sm'>
          {aspecto.titulo}
        </Text>
        {evento.derived && (
          <Tooltip
            label='Reconstruido a partir de la reserva, porque es anterior al registro de eventos. La hora puede ser aproximada.'
            placement='top'
            hasArrow
          >
            <Badge colorScheme='purple' variant='subtle' fontSize='0.6rem'>
              reconstruido
            </Badge>
          </Tooltip>
        )}
      </HStack>

      <Text fontSize='sm' color='gray.700' mt={0.5}>
        {evento.summary}
      </Text>

      <VStack align='stretch' spacing={1} mt={1.5}>
        <Autor evento={evento} />
        <Origen evento={evento} />
        <Tooltip
          label={evento.notaFecha ? evento.notaFecha.detalle : absoluta}
          placement='top-start'
          hasArrow
          openDelay={300}
        >
          <HStack spacing={1.5} color='gray.400' w='fit-content'>
            <Icon as={FiCalendar} boxSize={3.5} />
            <Text fontSize='xs'>
              {relativa ? `${relativa} · ${absoluta}` : absoluta}
            </Text>
          </HStack>
        </Tooltip>
      </VStack>

      <Cambios evento={evento} />
    </MotionBox>
  );
};

type Props = {
  reserva?: Record<string, any> | null;
  isOpen: boolean;
  onClose: () => void;
};

/**
 * Todo lo que le ha pasado a una reserva, de un vistazo.
 *
 * Es la respuesta a "quién hizo esto y desde dónde": antes había que rebuscar
 * en la pantalla de registros del superadmin, a la que el cliente ni siquiera
 * tiene acceso.
 */
const HistorialReserva = ({ reserva, isOpen, onClose }: Props) => {
  const { eventos, cargando } = useHistorialReserva(isOpen ? reserva : null);

  return (
    <Drawer isOpen={isOpen} placement='right' onClose={onClose} size='md'>
      <DrawerOverlay backdropFilter='blur(2px)' />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader borderBottomWidth='1px' pb={3}>
          <Text fontSize='md'>Historial de la reserva</Text>
          <HStack spacing={2} mt={1.5} flexWrap='wrap'>
            {reserva?.code && (
              <Badge colorScheme='blue' fontSize='0.7rem'>
                {reserva.code}
              </Badge>
            )}
            <Text fontSize='sm' fontWeight='normal' color='gray.600'>
              {reserva?.namesAndSurnames ?? reserva?.name ?? 'Sin nombre'}
            </Text>
            {reserva?.location?.name && (
              <Text fontSize='sm' fontWeight='normal' color='gray.400'>
                · {reserva.location.name}
              </Text>
            )}
          </HStack>
        </DrawerHeader>

        <DrawerBody py={6}>
          {cargando && (
            <VStack align='stretch' spacing={5}>
              {[0, 1, 2].map((i) => (
                <HStack key={i} spacing={4}>
                  <Skeleton borderRadius='full' w='32px' h='32px' />
                  <VStack align='stretch' flex={1} spacing={2}>
                    <Skeleton h='12px' w='55%' />
                    <Skeleton h='10px' w='75%' />
                  </VStack>
                </HStack>
              ))}
            </VStack>
          )}

          {!cargando && eventos.length === 0 && (
            <VStack py={12} spacing={2} color='gray.500'>
              <Icon as={FiCalendar} boxSize={7} color='gray.300' />
              <Text fontSize='sm'>Todavía no hay nada registrado de esta reserva.</Text>
            </VStack>
          )}

          {!cargando &&
            eventos.map((evento, i) => (
              <Evento
                key={evento.id ?? `${evento.type}-${i}`}
                evento={evento}
                indice={i}
                ultimo={i === eventos.length - 1}
              />
            ))}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};

export default HistorialReserva;
